import React, { useEffect, useState } from "react";
import { Row, Col } from "reactstrap";
import { payloadToObject } from "../Components/notificationMonitorUtils";

function NotificationPayloadInspector({
    isOpen,
    toggle,
    selectedPayload,
    notificationType
}) {
    const [expandedSections, setExpandedSections] = useState({});
    const [payloadView, setPayloadView] = useState("json");

    useEffect(() => {
        setExpandedSections({});
        setPayloadView("json");
    }, [selectedPayload, notificationType, isOpen]);

    if (!isOpen) return null;

    const toggleSection = (key) => {
        setExpandedSections((prev) => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const isExpanded = (key) =>
        expandedSections[key] === true;

    const topLevelSections = {
        ANY_OFFER_CHANGED: [
            /^Offers\s+\d+$/i,
            /^Summary$/i,
            /^Offer Change Trigger$/i
        ],
        B2B_ANY_OFFER_CHANGED: [
            /^Offers\s+\d+$/i,
            /^Summary$/i,
            /^Offer Change Trigger$/i
        ],
        FBA_INVENTORY_AVAILABILITY_CHANGES: [
            /^Fulfillment Inventory By Marketplace\s+\d+$/i
        ],
        ORDER_CHANGE: [
            /^Summary$/i,
            /^Order Change Trigger$/i
        ],
        PRICING_HEALTH: [
            /^Summary$/i,
            /^Merchant Offer$/i,
            /^Offer Change Trigger$/i
        ],
        ACCOUNT_STATUS_CHANGED: [
            /^Account Status Change Notification$/i
        ]
    };

    const isTopLevelSection = (label) => {
        const patterns =
            topLevelSections[notificationType] || [];

        return patterns.some((pattern) =>
            pattern.test(String(label))
        );
    };

    const Arrow = ({ open }) => (
        <span
            className={`notification-payload-arrow ${
                open
                    ? "arrow-open"
                    : "arrow-closed"
            }`}
        />
    );

    const SectionHeader = ({
        label,
        sectionKey
    }) => {
        const open = isExpanded(sectionKey);

        return (
            <div
                onClick={() =>
                    toggleSection(sectionKey)
                }
                className="notification-payload-section-header d-flex justify-content-between align-items-center"
            >
                <span>{label}</span>
                <Arrow open={open} />
            </div>
        );
    };

    const ValueRow = ({ item }) => (
        <div className="notification-payload-value-row">
            {item.label &&
                item.label !== ":" && (
                    <b>
                        {item.label}
                        &nbsp;:&nbsp;&nbsp;&nbsp;
                    </b>
                )}

            {item.value !== null &&
            item.value !== undefined &&
            item.value !== ""
                ? String(item.value)
                : "-"}
        </div>
    );

    const ChildHeader = ({ label }) => (
        <div className="notification-payload-child-header">
            {label}
        </div>
    );

    const createSections = () => {
        const sections = [];
        let currentSection = null;

        (selectedPayload || []).forEach((item) => {
            if (
                item.isHeader &&
                isTopLevelSection(item.label)
            ) {
                currentSection = {
                    label: item.label,
                    items: []
                };

                sections.push(currentSection);
            } else if (currentSection) {
                currentSection.items.push(item);
            } else {
                sections.push({
                    label: null,
                    items: [item]
                });

                currentSection =
                    sections[sections.length - 1];
            }
        });

        return sections;
    };

    const renderOfferSection = (
        section,
        sectionIndex
    ) => {
        const sectionKey =
            `offer-${sectionIndex}`;

        const open =
            isExpanded(sectionKey);

        return (
            <div
                key={sectionKey}
                className="notification-payload-section mb-2 border rounded"
            >
                <SectionHeader
                    label={section.label}
                    sectionKey={sectionKey}
                />

                {open && (
                    <div className="notification-payload-content">
                        {section.items.map(
                            (item, index) =>
                                item.isHeader ? (
                                    <ChildHeader
                                        key={index}
                                        label={item.label}
                                    />
                                ) : (
                                    <ValueRow
                                        key={index}
                                        item={item}
                                    />
                                )
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderAccountStatusChanged = () => (
        <div className="notification-payload-section border rounded">
            <div className="notification-payload-static-header">
                Account Status Change Notification
            </div>

            <div className="notification-payload-content">
                {(selectedPayload || []).map(
                    (item, index) => {
                        if (
                            item.isHeader &&
                            item.label ===
                                "Account Status Change Notification"
                        ) {
                            return null;
                        }

                        if (item.isHeader) {
                            return (
                                <ChildHeader
                                    key={index}
                                    label={item.label}
                                />
                            );
                        }

                        return (
                            <ValueRow
                                key={index}
                                item={item}
                            />
                        );
                    }
                )}
            </div>
        </div>
    );

    const renderNormalSection = (
        section,
        sectionIndex
    ) => {
        if (!section.label) {
            return section.items.map(
                (item, index) =>
                    item.isHeader ? (
                        <ChildHeader
                            key={index}
                            label={item.label}
                        />
                    ) : (
                        <ValueRow
                            key={index}
                            item={item}
                        />
                    )
            );
        }

        const sectionKey =
            `section-${sectionIndex}`;

        const open =
            isExpanded(sectionKey);

        return (
            <div
                key={sectionKey}
                className="notification-payload-section mb-2 border rounded"
            >
                <SectionHeader
                    label={section.label}
                    sectionKey={sectionKey}
                />

                {open && (
                    <div className="notification-payload-content">
                        {section.items.map(
                            (item, index) =>
                                item.isHeader ? (
                                    <ChildHeader
                                        key={index}
                                        label={item.label}
                                    />
                                ) : (
                                    <ValueRow
                                        key={index}
                                        item={item}
                                    />
                                )
                        )}
                    </div>
                )}
            </div>
        );
    };

    const payloadObj =
        payloadToObject(selectedPayload);

    const simpleEntries =
        Object.entries(payloadObj);

    const hasStructured =
        Array.isArray(selectedPayload) &&
        selectedPayload.length > 0;

    const sections = hasStructured
        ? createSections()
        : [];

    const renderSimpleDataView = () => {
        if (simpleEntries.length === 0) {
            return <div>No Payload Found</div>;
        }

        return (
            <div className="anm-payload-data-list">
                {simpleEntries.map(
                    ([key, value]) => (
                        <Row
                            key={key}
                            className="anm-data-row"
                        >
                            <Col xs="5">
                                <span className="anm-data-label">
                                    {key}
                                </span>
                            </Col>

                            <Col
                                xs="7"
                                className="text-end"
                            >
                                <span className="anm-data-value">
                                    {Array.isArray(value)
                                        ? value.join(", ")
                                        : String(
                                              value == null
                                                  ? "-"
                                                  : value
                                          )}
                                </span>
                            </Col>
                        </Row>
                    )
                )}
            </div>
        );
    };

    const renderStructuredDataView = () => {
        if (!hasStructured) {
            return renderSimpleDataView();
        }

        if (
            notificationType ===
            "ACCOUNT_STATUS_CHANGED"
        ) {
            return (
                <div className="notification-payload-container">
                    {renderAccountStatusChanged()}
                </div>
            );
        }

        return (
            <div className="notification-payload-container">
                {sections.map(
                    (section, index) => {
                        if (
                            (
                                notificationType ===
                                    "ANY_OFFER_CHANGED" ||
                                notificationType ===
                                    "B2B_ANY_OFFER_CHANGED"
                            ) &&
                            section.label &&
                            /^Offers\s+\d+$/i.test(
                                section.label
                            )
                        ) {
                            return renderOfferSection(
                                section,
                                index
                            );
                        }

                        return renderNormalSection(
                            section,
                            index
                        );
                    }
                )}
            </div>
        );
    };

    return (
        <div
            className="anm-modal-backdrop"
            onClick={(e) => {
                if (
                    e.target ===
                    e.currentTarget
                ) {
                    toggle();
                }
            }}
        >
            <div className="anm-modal-content">

                <div className="anm-modal-header">
                    <div>
                        <h3>
                            Notification Payload Inspector
                        </h3>

                        <div className="anm-modal-subtitle">
                            Inspect raw JSON or structured Data View
                            {notificationType
                                ? ` · ${notificationType}`
                                : ""}
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <div className="anm-toggle-group">
                            <button
                                type="button"
                                className={`anm-action-btn${
                                    payloadView ===
                                    "json"
                                        ? " active-toggle"
                                        : ""
                                }`}
                                onClick={() =>
                                    setPayloadView(
                                        "json"
                                    )
                                }
                            >
                                JSON
                            </button>

                            <button
                                type="button"
                                className={`anm-action-btn${
                                    payloadView ===
                                    "data"
                                        ? " active-toggle"
                                        : ""
                                }`}
                                onClick={() =>
                                    setPayloadView(
                                        "data"
                                    )
                                }
                            >
                                Data View
                            </button>
                        </div>

                        <button
                            type="button"
                            className="anm-close-btn"
                            onClick={toggle}
                        >
                            <i className="fas fa-times" />
                        </button>
                    </div>
                </div>

                <div className="anm-modal-body">
                    {payloadView === "json" ? (
                        <pre className="anm-payload-pre json">
                            {JSON.stringify(
                                payloadObj,
                                null,
                                2
                            )}
                        </pre>
                    ) : (
                        renderStructuredDataView()
                    )}
                </div>

                <div className="anm-modal-footer">
                    <button
                        type="button"
                        className="anm-btn anm-btn-primary"
                        onClick={toggle}
                    >
                        Close
                    </button>
                </div>

            </div>
        </div>
    );
}

export default NotificationPayloadInspector;